import React from "react";

type ChartSkeletonVariant =
  | "bar"
  | "line"
  | "area"
  | "pie"
  | "scatter"
  | "heatmap"
  | "treemap"
  | "gauge"
  | "vertical-gauge"
  | "map";

interface SkeletonProps {
  className?: string;
  testId?: string;
}

interface ChartLoadingSkeletonProps extends SkeletonProps {
  variant?: ChartSkeletonVariant;
}

function cx(...classes: Array<string | undefined | false>): string {
  return classes.filter(Boolean).join(" ");
}

function SkeletonBlock({ className }: { className: string }) {
  return (
    <div
      className={cx(
        "rounded bg-muted/70 dark:bg-muted/50",
        className,
      )}
    />
  );
}

function ChartFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-[160px] w-full flex-col justify-end rounded-md border border-border/60 bg-muted/20 p-4">
      {children}
    </div>
  );
}

function BarSkeleton() {
  const bars = [
    ["h-[36%]", "bar-a"],
    ["h-[68%]", "bar-b"],
    ["h-[48%]", "bar-c"],
    ["h-[82%]", "bar-d"],
    ["h-[58%]", "bar-e"],
    ["h-[42%]", "bar-f"],
  ];

  return (
    <ChartFrame>
      <div className="flex h-full items-end gap-3">
        {bars.map(([height, key]) => (
          <SkeletonBlock key={key} className={cx("flex-1", height)} />
        ))}
      </div>
      <SkeletonBlock className="mt-3 h-2 w-full" />
    </ChartFrame>
  );
}

function LineSkeleton({ filled = false }: { filled?: boolean }) {
  return (
    <ChartFrame>
      <div className="relative h-full min-h-[120px]">
        <SkeletonBlock className="absolute bottom-0 left-0 h-px w-full" />
        <SkeletonBlock className="absolute bottom-0 left-0 h-full w-px" />
        {filled && (
          <div className="absolute bottom-0 left-[8%] h-[54%] w-[84%] rounded-t-[40%] bg-muted/40 [clip-path:polygon(0_74%,18%_52%,38%_61%,58%_28%,78%_42%,100%_18%,100%_100%,0_100%)]" />
        )}
        <div className="absolute left-[8%] top-[18%] h-[4px] w-[84%] rounded-full bg-muted [clip-path:polygon(0_70%,18%_45%,38%_58%,58%_20%,78%_38%,100%_0,100%_100%,0_100%)]" />
        <SkeletonBlock className="absolute left-[7%] top-[65%] h-3 w-3 rounded-full" />
        <SkeletonBlock className="absolute left-[24%] top-[45%] h-3 w-3 rounded-full" />
        <SkeletonBlock className="absolute left-[45%] top-[54%] h-3 w-3 rounded-full" />
        <SkeletonBlock className="absolute left-[63%] top-[24%] h-3 w-3 rounded-full" />
        <SkeletonBlock className="absolute left-[83%] top-[36%] h-3 w-3 rounded-full" />
      </div>
    </ChartFrame>
  );
}

function PieSkeleton() {
  return (
    <div className="flex h-full min-h-[160px] w-full items-center justify-center rounded-md border border-border/60 bg-muted/20 p-4">
      <div className="relative h-28 w-28 rounded-full bg-muted/70">
        <div className="absolute left-1/2 top-1/2 h-14 w-14 -translate-x-1/2 -translate-y-1/2 rounded-full bg-background/80" />
        <div className="absolute right-0 top-0 h-14 w-14 rounded-tr-full bg-muted-foreground/10" />
      </div>
    </div>
  );
}

function ScatterSkeleton() {
  const dots = [
    ["left-[14%] top-[58%]", "dot-a"],
    ["left-[24%] top-[38%]", "dot-b"],
    ["left-[41%] top-[68%]", "dot-c"],
    ["left-[55%] top-[26%]", "dot-d"],
    ["left-[69%] top-[48%]", "dot-e"],
    ["left-[82%] top-[18%]", "dot-f"],
  ];

  return (
    <ChartFrame>
      <div className="relative h-full min-h-[120px]">
        <SkeletonBlock className="absolute bottom-0 left-0 h-px w-full" />
        <SkeletonBlock className="absolute bottom-0 left-0 h-full w-px" />
        {dots.map(([position, key]) => (
          <SkeletonBlock key={key} className={cx("absolute h-3 w-3 rounded-full", position)} />
        ))}
      </div>
    </ChartFrame>
  );
}

function HeatmapSkeleton() {
  const cells = Array.from({ length: 20 }, (_, index) => `cell-${index}`);

  return (
    <ChartFrame>
      <div className="grid h-full min-h-[120px] grid-cols-5 gap-2">
        {cells.map((key) => (
          <SkeletonBlock key={key} className="h-full min-h-5" />
        ))}
      </div>
    </ChartFrame>
  );
}

function TreemapSkeleton() {
  return (
    <ChartFrame>
      <div className="grid h-full min-h-[120px] grid-cols-5 grid-rows-3 gap-2">
        <SkeletonBlock className="col-span-2 row-span-2" />
        <SkeletonBlock className="col-span-3" />
        <SkeletonBlock className="col-span-2" />
        <SkeletonBlock className="col-span-1 row-span-2" />
        <SkeletonBlock className="col-span-2" />
        <SkeletonBlock className="col-span-2" />
      </div>
    </ChartFrame>
  );
}

function GaugeSkeleton() {
  return (
    <div
      className="flex h-full min-h-[160px] w-full items-center justify-center"
      data-testid="chart-loading-gauge-skeleton"
    >
      <div className="relative h-32 w-32 overflow-hidden">
        <div
          className="absolute inset-x-0 bottom-0 h-16 rounded-t-full border-[18px] border-b-0 border-muted/80"
          data-testid="chart-loading-gauge-arc"
        />
      </div>
    </div>
  );
}

function VerticalGaugeSkeleton() {
  return (
    <div className="flex h-full min-h-[180px] w-full items-center justify-center rounded-md border border-border/60 bg-muted/20 p-4">
      <div className="flex h-full min-h-[150px] w-28 flex-col items-center justify-center gap-3">
        <div className="relative h-full min-h-[128px] w-14 rounded-full border border-border/70 bg-muted/40 p-1">
          <div className="absolute bottom-1 left-1 right-1 h-2/3 rounded-full bg-muted/80" />
          <div className="absolute left-full top-[18%] ml-3 flex h-[64%] flex-col justify-between">
            <SkeletonBlock className="h-px w-5" />
            <SkeletonBlock className="h-px w-4" />
            <SkeletonBlock className="h-px w-5" />
            <SkeletonBlock className="h-px w-4" />
          </div>
        </div>
        <SkeletonBlock className="h-4 w-16 rounded-full" />
      </div>
    </div>
  );
}

function MapSkeleton() {
  return (
    <ChartFrame>
      <div className="relative h-full min-h-[140px] overflow-hidden rounded bg-muted/50">
        <div className="absolute left-[8%] top-[18%] h-16 w-28 rounded-full bg-background/30" />
        <div className="absolute bottom-[12%] right-[10%] h-20 w-32 rounded-full bg-background/30" />
        <SkeletonBlock className="absolute left-[48%] top-[38%] h-6 w-4 rounded-full" />
        <SkeletonBlock className="absolute left-[22%] top-[55%] h-5 w-3 rounded-full" />
      </div>
    </ChartFrame>
  );
}

export function ChartLoadingSkeleton({
  variant = "line",
  className,
  testId = "chart-loading-skeleton",
}: ChartLoadingSkeletonProps) {
  const content = (() => {
    switch (variant) {
      case "bar":
        return <BarSkeleton />;
      case "area":
        return <LineSkeleton filled />;
      case "pie":
        return <PieSkeleton />;
      case "scatter":
        return <ScatterSkeleton />;
      case "heatmap":
        return <HeatmapSkeleton />;
      case "treemap":
        return <TreemapSkeleton />;
      case "gauge":
        return <GaugeSkeleton />;
      case "vertical-gauge":
        return <VerticalGaugeSkeleton />;
      case "map":
        return <MapSkeleton />;
      case "line":
      default:
        return <LineSkeleton />;
    }
  })();

  return (
    <div
      className={cx("h-full w-full animate-pulse", className)}
      data-testid={testId}
      role="status"
      aria-busy="true"
      aria-label="Loading data"
    >
      {content}
    </div>
  );
}

export function TableLoadingSkeleton({
  className,
  testId = "table-loading-skeleton",
}: SkeletonProps) {
  const rows = ["row-a", "row-b", "row-c", "row-d"];
  const columns = ["col-a", "col-b", "col-c"];

  return (
    <div
      className={cx("w-full animate-pulse rounded-md border border-border/60 bg-muted/20 p-4", className)}
      data-testid={testId}
      role="status"
      aria-busy="true"
      aria-label="Loading table data"
    >
      <div className="grid grid-cols-3 gap-3 border-b border-border/60 pb-3">
        {columns.map((key) => (
          <SkeletonBlock key={key} className="h-4 w-3/4" />
        ))}
      </div>
      <div className="space-y-3 pt-3">
        {rows.map((key) => (
          <div key={key} className="grid grid-cols-3 gap-3">
            <SkeletonBlock className="h-4 w-full" />
            <SkeletonBlock className="h-4 w-5/6" />
            <SkeletonBlock className="h-4 w-2/3" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function DataCardLoadingSkeleton({
  className,
  testId = "data-card-loading-skeleton",
}: SkeletonProps) {
  return (
    <div
      className={cx("flex min-h-[84px] w-full animate-pulse flex-col justify-center gap-3", className)}
      data-testid={testId}
      role="status"
      aria-busy="true"
      aria-label="Loading data card"
    >
      <SkeletonBlock className="h-3 w-24" />
      <SkeletonBlock className="h-8 w-32" />
      <SkeletonBlock className="h-3 w-20" />
    </div>
  );
}

export function DataTextLoadingSkeleton({
  className,
  testId = "data-text-loading-skeleton",
}: SkeletonProps) {
  return (
    <div
      className={cx("flex w-full animate-pulse flex-col gap-2", className)}
      data-testid={testId}
      role="status"
      aria-busy="true"
      aria-label="Loading data text"
    >
      <SkeletonBlock className="h-3 w-20" />
      <SkeletonBlock className="h-6 w-28" />
    </div>
  );
}
